import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { toast } from "sonner";
import {
  ChartLine, CheckCircle, Clock, Warning, Sparkle, ArrowRight, X, Lock,
  Coins, CurrencyDollar, Plus, LinkSimple, Trash, Lightning, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Input } from "../ui/input";
import BridgeControls from "./BridgeControls";
import MT5CredentialsFields from "./MT5CredentialsFields";

const PLAN_META = [
  { code: "starter",   label: "Starter Plan",          days: 28,  free_days: 0,  tagline: "Get started · 28 days at one base cycle" },
  { code: "quarterly", label: "Quarterly Value Plan",  days: 84,  free_days: 7,  tagline: "3 cycles bundled · 7 free days" },
  { code: "half_year", label: "Half-Year Alpha Plan",  days: 168, free_days: 21, tagline: "6 cycles · 3 free weeks" },
  { code: "annual",    label: "Annual Elite Plan",     days: 365, free_days: 45, tagline: "Full year · ~6 free weeks" },
];

const ACCOUNT_META = {
  cent: { label: "CENT Account", icon: Coins, accent: "#F59E0B", desc: "Micro-lot trading in cents (USD)" },
  usd: { label: "USD Account", icon: CurrencyDollar, accent: "#10B981", desc: "Standard USD trading account" },
};

function statusPill(status) {
  const map = {
    paid_pending_approval: { label: "Active (Unverified)", color: "bg-sky-500/10 text-sky-300 border-sky-500/30", icon: Clock },
    active_unverified: { label: "Active (Unverified)", color: "bg-sky-500/10 text-sky-300 border-sky-500/30", icon: Clock },
    verified: { label: "Verified", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: CheckCircle },
    active: { label: "Verified", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: CheckCircle },
    expired: { label: "Expired", color: "bg-zinc-600/20 text-zinc-400 border-zinc-600/30", icon: Warning },
    rejected: { label: "Rejected", color: "bg-rose-500/10 text-rose-400 border-rose-500/30", icon: X },
  };
  const s = map[status];
  if (!s) return null;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${s.color}`}>
      <Icon size={10} weight="bold" />
      {s.label}
    </span>
  );
}

function daysRemaining(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  const d = Math.ceil(diff / (86400000));
  return d;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function PlanPicker({ accountType, open, onClose, onSuccess, profile }) {
  const [pricing, setPricing] = useState([]);
  const [currencyMode, setCurrencyMode] = useState("INR");
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [broker, setBroker] = useState("Exness");
  const [loginId, setLoginId] = useState("");
  const [pwd, setPwd] = useState("");
  const [server, setServer] = useState("");
  const [joiningFee, setJoiningFee] = useState(2999);
  const [joiningFeePaid, setJoiningFeePaid] = useState(false);
  const [linkFeePreview, setLinkFeePreview] = useState(300);
  const [submitting, setSubmitting] = useState(false);
  const [existingAcct, setExistingAcct] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${AFF_API}/affiliate/mt5/pricing`, { headers: getAffHeaders() })
      .then((r) => {
        setPricing(r.data.plans.filter((p) => p.account_type === accountType));
        setJoiningFee(r.data.joining_fee || 2999);
        setCurrencyMode(r.data.currency_mode || "INR");
      });
    axios.get(`${AFF_API}/affiliate/mt5/accounts`, { headers: getAffHeaders() })
      .then((r) => {
        setJoiningFeePaid(!!r.data.joining_fee_paid_at);
        const acct = (r.data.accounts || {})[accountType];
        setExistingAcct(acct || null);
        // Pre-fill last known creds so renewals are one-click
        if (acct) {
          setBroker(acct.broker_name || "Exness");
          setLoginId(acct.mt5_login_id || "");
          setServer(acct.mt5_server || "");
        }
      });
  }, [open, accountType]);

  // ₹300 link fee preview — applies when:
  //   • No existing account on this slot (first link), OR
  //   • broker / login / server differ from existing (= different MT5 account).
  // Password-only changes (security rotation) do NOT trigger ₹300 on renewal.
  // Mirrors backend logic in payments.create_order.
  useEffect(() => {
    if (!existingAcct) { setLinkFeePreview(300); return; }
    const immutableMatch =
      existingAcct.broker_name === broker.trim() &&
      existingAcct.mt5_login_id === loginId.trim() &&
      existingAcct.mt5_server === server.trim();
    const isRenewal = immutableMatch && !!existingAcct.link_fee_paid;
    setLinkFeePreview(isRenewal ? 0 : 300);
  }, [existingAcct, broker, loginId, server]);

  const plan = pricing.find((p) => p.plan === selectedPlan);
  const planFee = plan?.price_inr || 0;
  const total = planFee + (joiningFeePaid ? 0 : joiningFee) + linkFeePreview;
  const isUSD = currencyMode === "USD";
  const usdFor = (inr) => {
    // Reverse-map INR-equivalent to USD sticker for common tiers
    const map = { 2435: 29, 6635: 79, 12515: 149, 25115: 299, 8299: 99 };
    return map[inr] ?? Math.round(inr / 84);
  };
  const fmt = (inr) => (isUSD ? `$${usdFor(inr)}` : `₹${Number(inr).toLocaleString("en-IN")}`);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!broker.trim() || !loginId.trim() || !pwd.trim() || !server.trim()) {
      toast.error("Fill all MT5 broker fields"); return;
    }
    setSubmitting(true);
    try {
      // 1) Create order
      const orderRes = await axios.post(
        `${AFF_API}/affiliate/mt5/${accountType}/create-order`,
        { plan: selectedPlan, broker_name: broker.trim(), mt5_login_id: loginId.trim(), mt5_password: pwd, mt5_server: server.trim() },
        { headers: getAffHeaders() }
      );
      const { order_id, amount_paise, key_id, stub } = orderRes.data;

      // 2a) Stub mode: directly call verify
      if (stub) {
        await axios.post(
          `${AFF_API}/affiliate/mt5/${accountType}/verify`,
          { razorpay_order_id: order_id, razorpay_payment_id: `stub_pay_${Date.now()}` },
          { headers: getAffHeaders() }
        );
        toast.success(`Payment recorded (test-stub). Your ${accountType.toUpperCase()} account is awaiting admin approval.`);
        onSuccess();
        return;
      }

      // 2b) Real Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Razorpay SDK failed to load"); return; }
      const rzp = new window.Razorpay({
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount_paise,
        currency: "INR",
        name: "VeloxAlgoTech LLP Affiliate Platform",
        description: `${ACCOUNT_META[accountType].label} · ${plan.label} plan`,
        order_id: order_id,
        prefill: {
          name: profile?.name || "",
          email: profile?.email || "",
          contact: profile?.phone || "",
        },
        theme: { color: "#00E5FF" },
        handler: async (response) => {
          try {
            await axios.post(
              `${AFF_API}/affiliate/mt5/${accountType}/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAffHeaders() }
            );
            toast.success(`Payment successful! Your ${accountType.toUpperCase()} account is awaiting admin approval.`);
            onSuccess();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const Icon = ACCOUNT_META[accountType].icon;
  const accent = ACCOUNT_META[accountType].accent;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={`plan-picker-${accountType}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-white/10 bg-zinc-950 p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A` }}>
              <Icon size={22} weight="duotone" style={{ color: accent }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Activate {ACCOUNT_META[accountType].label}</h2>
              <p className="text-[11px] text-zinc-500">{ACCOUNT_META[accountType].desc}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid={`plan-picker-close-${accountType}`} className="p-1 text-zinc-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Plan selection */}
        <div className="mb-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">Choose a Plan</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid={`plan-picker-${accountType}`}>
            {PLAN_META.map((p) => {
              const match = pricing.find((x) => x.plan === p.code);
              const isBest = p.code === "annual";
              const active = selectedPlan === p.code;
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => setSelectedPlan(p.code)}
                  data-testid={`plan-option-${accountType}-${p.code}`}
                  className={`relative p-3 rounded-lg border text-left transition-all overflow-hidden ${
                    active
                      ? "border-[#00E5FF] bg-[#00E5FF]/8 shadow-[0_0_18px_rgba(0,229,255,0.15)]"
                      : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                  }`}
                >
                  {isBest && (
                    <span className="absolute -top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-[#F59E0B] text-black">
                      <Sparkle size={8} weight="fill" /> Best Value
                    </span>
                  )}
                  <p className="text-xs font-bold text-white">{p.label}</p>
                  <p className="text-[9px] text-zinc-500 mt-0.5 leading-snug">{p.tagline}</p>
                  <p className="text-lg font-black text-[#00E5FF] mt-2 font-mono">{match ? fmt(match.price_inr) : "—"}</p>
                  <p className="text-[9px] text-emerald-400/80 mt-0.5">{p.days} days · all-inclusive (no extra GST)</p>
                  {p.free_days > 0 && (
                    <span
                      data-testid={`plan-freedays-${p.code}`}
                      className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    >
                      + {p.free_days} free days
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* MT5 details */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Broker Details</p>
          <MT5CredentialsFields
            broker={broker} setBroker={setBroker}
            server={server} setServer={setServer}
            loginId={loginId} setLoginId={setLoginId}
            pwd={pwd} setPwd={setPwd}
            testidPrefix={`pp-${accountType}`}
          />

          {/* Price breakdown */}
          <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400">{plan?.label || ""} subscription</span>
              <span className="text-white font-mono">{fmt(planFee)}</span>
            </div>
            {!joiningFeePaid && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400">Platform joining fee (one-time)</span>
                <span className="text-white font-mono">{fmt(joiningFee)}</span>
              </div>
            )}
            {linkFeePreview > 0 ? (
              <div className="flex items-center justify-between text-xs" data-testid={`pp-link-fee-${accountType}`}>
                <span className="text-zinc-400">
                  MT5 link fee (one-time) — {existingAcct ? "different account" : "new account"}
                </span>
                <span className="text-white font-mono">{fmt(linkFeePreview)}</span>
              </div>
            ) : existingAcct ? (
              <div className="flex items-center justify-between text-[11px]" data-testid={`pp-link-fee-waived-${accountType}`}>
                <span className="text-emerald-300">✓ Link fee waived (subscription renewal)</span>
                <span className="text-emerald-300 font-mono">{fmt(0)}</span>
              </div>
            ) : null}
            <div className="h-px bg-white/5 my-1.5"></div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white">Total payable</span>
              <span className="text-lg font-black text-[#00E5FF] font-mono" data-testid={`pp-total-${accountType}`}>{fmt(total)}</span>
            </div>
            <p className="text-[10px] text-emerald-400/80 mt-0.5 text-right">{isUSD ? "All-inclusive · No GST (Non-India)" : "All-inclusive · No additional GST"}</p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid={`pp-pay-${accountType}`}
            className="w-full py-3 rounded-md text-sm font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? "Processing..." : <>Pay {fmt(total)} with Razorpay <ArrowRight size={14} weight="bold" /></>}
          </button>
          <p className="text-[10px] text-zinc-600 text-center">
            Secure checkout via Razorpay · UPI · Cards · Netbanking
          </p>
        </form>
      </div>
    </div>
  );
}

function AccountCard({ type, acct, profile, onPaid }) {
  const meta = ACCOUNT_META[type];
  const Icon = meta.icon;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const remaining = daysRemaining(acct?.expires_at);

  const isRejected = acct?.status === "rejected";
  // MetaApi-side auth failure — broker rejected the creds even though
  // admin verified the account on paper. The user must re-submit with
  // correct creds (₹300 re-link fee) before trades can be copied.
  const bridgeErr = acct?.bridge?.last_error || acct?.bridge?.copyfactory_error || "";
  const hasAuthError = !!bridgeErr && (
    /invalid account|invalid credential|authenticate to your broker|password|server|not found/i.test(bridgeErr)
  );
  const needsRelink = isRejected || (hasAuthError && !isRejected);
  const needsAction = !acct || ["expired"].includes(acct?.status);
  const isActive = ["active", "verified", "active_unverified"].includes(acct?.status);
  const btnLabel = !acct
    ? `Activate ${meta.label.split(" ")[0]}`
    : isActive
    ? "Renew / Upgrade"
    : acct.status === "expired"
    ? "Renew Now"
    : "";

  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5" data-testid={`mt5-card-${type}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${meta.accent}1A` }}>
            <Icon size={18} weight="duotone" style={{ color: meta.accent }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">{meta.label}</h3>
            <p className="text-[10px] text-zinc-500">{meta.desc}</p>
          </div>
        </div>
        {acct?.status && statusPill(acct.status)}
      </div>

      {acct ? (
        <div className="space-y-2 text-xs mb-4">
          <div className="flex justify-between"><span className="text-zinc-500">Broker</span><span className="text-white font-medium">{acct.broker_name}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">MT5 Login</span><span className="text-white font-mono">{acct.mt5_login_id}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Server</span><span className="text-white font-mono">{acct.mt5_server}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Plan</span><span className="text-white font-bold">{PLAN_META.find((p) => p.code === acct.plan)?.label || acct.plan}</span></div>
          {acct.status === "active_unverified" && (
            <div className="p-2 rounded-md bg-sky-500/5 border border-sky-500/20 text-[11px] text-sky-200">
              Trading is enabled. Admin will verify your account within a few days.
            </div>
          )}
          {(acct.status === "active" || acct.status === "verified") && remaining != null && (
            <div className="flex justify-between">
              <span className="text-zinc-500">Time Remaining</span>
              <span className={`font-bold font-mono ${remaining <= 7 ? "text-rose-400" : remaining <= 30 ? "text-amber-400" : "text-emerald-400"}`}>
                {remaining} {remaining === 1 ? "day" : "days"}
              </span>
            </div>
          )}
          {acct.status === "expired" && (
            <div className="p-2 rounded-md bg-rose-500/5 border border-rose-500/20 text-[11px] text-rose-300">
              Your subscription expired. Renew below to resume trading.
            </div>
          )}
          {acct.status === "rejected" && (
            <div className="p-3 rounded-md bg-rose-500/5 border border-rose-500/30 text-[11px] text-rose-200 space-y-2" data-testid={`mt5-rejected-banner-${type}`}>
              <p className="font-semibold">Account rejected — trading stopped.</p>
              {acct.rejection_reason && <p className="text-zinc-400">Reason: {acct.rejection_reason}</p>}
              <p className="text-zinc-400">Enter new MT5 details below to resume.</p>
            </div>
          )}
          {hasAuthError && !isRejected && (
            <div className="p-3 rounded-md bg-amber-500/5 border border-amber-500/40 text-[11px] text-amber-200 space-y-2" data-testid={`mt5-autherr-banner-${type}`}>
              <p className="font-semibold flex items-center gap-1.5">
                <Warning size={12} weight="bold" /> Broker rejected your MT5 credentials
              </p>
              <p className="text-zinc-300 break-words font-mono text-[10px]">{bridgeErr}</p>
              <p className="text-zinc-400 leading-relaxed">
                Your subscription is active, but the broker refused the login. This usually means:
                <span className="block mt-1">• Wrong password OR</span>
                <span className="block">• Wrong server (your account is on a different one, e.g. Real7 instead of Real6) OR</span>
                <span className="block">• Login number typed incorrectly</span>
              </p>
              <p className="text-zinc-400">Click <span className="font-semibold text-amber-200">Re-link MT5</span> below — a ₹300 re-link fee applies. Once fixed, copy-trading starts within ~60 seconds.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 rounded-md bg-white/[0.02] border border-dashed border-white/10 mb-4 text-center">
          <Lock size={20} className="text-zinc-600 mx-auto mb-1" />
          <p className="text-xs text-zinc-400">Not activated yet</p>
          <p className="text-[10px] text-zinc-600 mt-0.5">Plans starting from ₹499</p>
        </div>
      )}

      {(needsAction || isActive) && (
        <button
          onClick={() => setPickerOpen(true)}
          data-testid={`mt5-activate-${type}`}
          className="w-full py-2.5 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-all"
        >
          {btnLabel}
        </button>
      )}

      {needsRelink && (
        <button
          onClick={() => setResubmitOpen(true)}
          data-testid={`mt5-resubmit-${type}`}
          className="w-full mt-2 py-2.5 rounded-md text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 transition-all"
        >
          {isRejected ? "Submit New MT5 Details" : "Re-link MT5 — Pay ₹300"}
        </button>
      )}

      <ResubmitDialog
        accountType={type}
        open={resubmitOpen}
        onClose={() => setResubmitOpen(false)}
        onSuccess={() => { setResubmitOpen(false); onPaid && onPaid(); }}
      />

      <PlanPicker
        accountType={type}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSuccess={() => { setPickerOpen(false); onPaid(); }}
        profile={profile}
      />
    </div>
  );
}

function ResubmitDialog({ accountType, open, onClose, onSuccess }) {
  const [broker, setBroker] = useState("Exness");
  const [loginId, setLoginId] = useState("");
  const [pwd, setPwd] = useState("");
  const [server, setServer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      // 1) Create ₹300 link-fee order for the resubmit
      const orderRes = await axios.post(
        `${AFF_API}/affiliate/mt5-link-fee/create-order`,
        {
          action: `resubmit_${accountType}`,
          broker_name: broker,
          mt5_login_id: loginId,
          mt5_password: pwd,
          mt5_server: server,
        },
        { headers: getAffHeaders() },
      );
      const { order_id, amount_paise, key_id, stub } = orderRes.data;

      // 2a) Stub mode: verify directly
      if (stub) {
        await axios.post(
          `${AFF_API}/affiliate/mt5-link-fee/verify`,
          { razorpay_order_id: order_id, razorpay_payment_id: `stub_pay_${Date.now()}` },
          { headers: getAffHeaders() },
        );
        toast.success("New MT5 details submitted (₹300 link fee — test-stub). Trading resumed.");
        onSuccess();
        return;
      }

      // 2b) Live Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) { setError("Razorpay SDK failed to load"); return; }
      const rzp = new window.Razorpay({
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount_paise,
        currency: "INR",
        name: "VeloxAlgoTech LLP Affiliate Platform",
        description: `MT5 Re-link Fee · ${accountType.toUpperCase()} (${broker})`,
        order_id,
        theme: { color: "#F59E0B" },
        handler: async (response) => {
          try {
            await axios.post(
              `${AFF_API}/affiliate/mt5-link-fee/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAffHeaders() },
            );
            toast.success("New MT5 details submitted (₹300 link fee paid). Trading resumed.");
            onSuccess();
          } catch (err) {
            setError(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={`resubmit-dialog-${accountType}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl border border-amber-500/30 bg-zinc-950 p-6">
        <h3 className="text-base font-bold text-white mb-1">Resubmit MT5 Details</h3>
        <p className="text-[11px] text-zinc-400 mb-4">
          Your previous MT5 account was rejected. A ₹300 re-link fee applies. Enter the corrected details below — trading will resume immediately while admin re-verifies.
        </p>
        {error && <div className="mb-3 p-2 rounded bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300">{error}</div>}
        <form onSubmit={submit} className="space-y-3">
          <MT5CredentialsFields
            broker={broker} setBroker={setBroker}
            server={server} setServer={setServer}
            loginId={loginId} setLoginId={setLoginId}
            pwd={pwd} setPwd={setPwd}
            testidPrefix={`resubmit-${accountType}`}
          />
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-md border border-white/10 text-xs text-zinc-300 hover:border-white/30">
              Cancel
            </button>
            <button type="submit" disabled={busy} data-testid={`resubmit-submit-${accountType}`} className="flex-1 py-2 rounded-md bg-amber-400 hover:bg-amber-300 text-black text-xs font-semibold disabled:opacity-50">
              {busy ? "Processing…" : "Pay ₹300 & Resubmit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MT5Accounts({ profile, onUpdate }) {
  const [accounts, setAccounts] = useState({ cent: null, usd: null });
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${AFF_API}/affiliate/mt5/accounts`, { headers: getAffHeaders() }),
        axios.get(`${AFF_API}/affiliate/mt5/extras`, { headers: getAffHeaders() }),
      ]);
      setAccounts(r1.data.accounts || { cent: null, usd: null });
      setExtras(r2.data.extras || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleRemoveExtra = async (id) => {
    try {
      await axios.delete(`${AFF_API}/affiliate/mt5/extras/${id}`, { headers: getAffHeaders() });
      toast.success("Account unlinked");
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unlink");
    }
  };

  if (loading) {
    return <div className="h-40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const hasAnyAccount = accounts.cent || accounts.usd;

  return (
    <div className="space-y-3" data-testid="mt5-accounts">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ChartLine size={16} weight="duotone" className="text-[#00E5FF]" />
          <h2 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>MT5 Trading Accounts</h2>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          data-testid="mt5-add-account-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-sky-500/10 border border-sky-500/30 text-sky-300 hover:bg-sky-500/20 transition-colors"
        >
          <Plus size={11} weight="bold" /> Add Account
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AccountCard type="cent" acct={accounts.cent} profile={profile} onPaid={() => { fetchAccounts(); onUpdate && onUpdate(); }} />
        <AccountCard type="usd" acct={accounts.usd} profile={profile} onPaid={() => { fetchAccounts(); onUpdate && onUpdate(); }} />
      </div>

      {/* Account Management & Subscription Status — quick actions per linked account */}
      {hasAnyAccount && (
        <AccountManagementBox
          accounts={accounts}
          profile={profile}
          onRefresh={() => { fetchAccounts(); onUpdate && onUpdate(); }}
        />
      )}

      {/* MT5 Bridge — copy trading */}
      <BridgeControls accounts={accounts} />

      {extras.length > 0 && (
        <div className="space-y-2 pt-1" data-testid="mt5-extras-list">
          <h3 className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Additional Linked Accounts ({extras.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {extras.map((e) => (
              <div
                key={e.id}
                data-testid={`mt5-extra-${e.id}`}
                className="p-3 rounded-lg border border-zinc-800/60 bg-zinc-900/40 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{e.nickname}</div>
                  <div className="text-[10px] font-mono text-zinc-400 truncate">
                    {e.broker_name} · {e.account_type.toUpperCase()} · #{e.mt5_login_id}
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">Server: {e.mt5_server}</div>
                </div>
                <button
                  onClick={() => handleRemoveExtra(e.id)}
                  data-testid={`mt5-extra-remove-${e.id}`}
                  className="text-zinc-500 hover:text-rose-400 transition-colors"
                  aria-label="Unlink account"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddExtraDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); fetchAccounts(); }} />
    </div>
  );
}

// ==================== ACCOUNT MANAGEMENT BOX ====================
// Renders directly below the CENT/USD cards. For each linked primary
// account it surfaces:
//   • The MT5 login number (prominent)
//   • A dynamic status pill ("Active & Provisioned" / "Expires in N days" /
//     "Expired — paused" etc.)
//   • Three actions: Topup / Renew, Connect (update creds), Delete account.
function AccountManagementBox({ accounts, profile, onRefresh }) {
  const rows = [];
  if (accounts.cent) rows.push({ type: "cent", acct: accounts.cent });
  if (accounts.usd) rows.push({ type: "usd", acct: accounts.usd });
  if (!rows.length) return null;

  return (
    <div
      data-testid="account-mgmt-box"
      className="rounded-xl border border-white/10 bg-gradient-to-br from-zinc-900/60 via-zinc-900/40 to-zinc-950/60 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={14} weight="duotone" className="text-[#00E5FF]" />
        <h3 className="text-xs font-bold text-white tracking-wide">Account Management &amp; Subscription Status</h3>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <AccountMgmtRow key={r.type} type={r.type} acct={r.acct} profile={profile} onRefresh={onRefresh} />
        ))}
      </div>
    </div>
  );
}

function AccountMgmtRow({ type, acct, profile, onRefresh }) {
  const meta = ACCOUNT_META[type];
  const Icon = meta.icon;
  const remaining = daysRemaining(acct?.expires_at);
  const isActive = ["active", "verified", "active_unverified"].includes(acct?.status);
  const isExpired = acct?.status === "expired";

  const [topupOpen, setTopupOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${AFF_API}/affiliate/mt5/${type}`, { headers: getAffHeaders() });
      toast.success(`${type.toUpperCase()} account unlinked. Cloud container released.`);
      setConfirmOpen(false);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  let statusBadge = null;
  if (isActive && remaining != null && remaining > 7) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
        <CheckCircle size={10} weight="bold" /> Active &amp; Provisioned
      </span>
    );
  } else if (isActive && remaining != null && remaining <= 7 && remaining > 0) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30">
        <Clock size={10} weight="bold" /> Expires in {remaining}d
      </span>
    );
  } else if (isExpired) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-500/15 text-rose-300 border border-rose-500/30">
        <Warning size={10} weight="bold" /> Expired — paused
      </span>
    );
  }

  return (
    <div
      data-testid={`mgmt-row-${type}`}
      className="rounded-lg border border-white/8 bg-white/[0.02] p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
    >
      {/* Left: account identity */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${meta.accent}1A` }}>
          <Icon size={16} weight="duotone" style={{ color: meta.accent }} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{meta.label}</span>
            {statusBadge}
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="text-[10px] text-zinc-500">MT5 Login</span>
            <span data-testid={`mgmt-login-${type}`} className="text-base font-black text-white font-mono tracking-wide">
              {acct.mt5_login_id || "—"}
            </span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
            {acct.broker_name} · {acct.mt5_server}
            {isActive && remaining != null && (
              <span className={`ml-2 font-mono font-bold ${remaining <= 7 ? "text-rose-400" : remaining <= 30 ? "text-amber-400" : "text-emerald-400"}`}>
                · {remaining}d left
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setTopupOpen(true)}
          data-testid={`mgmt-topup-${type}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/25 transition-colors"
        >
          <ArrowsClockwise size={11} weight="bold" />
          Topup / Renew
        </button>
        <button
          type="button"
          onClick={() => setConnectOpen(true)}
          data-testid={`mgmt-connect-${type}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-sky-500/10 border border-sky-500/30 text-sky-300 hover:bg-sky-500/20 transition-colors"
        >
          <LinkSimple size={11} weight="bold" />
          Connect Account
        </button>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          data-testid={`mgmt-delete-${type}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 transition-colors"
        >
          <Trash size={11} weight="bold" />
          Delete
        </button>
      </div>

      {/* Topup modal — clean plan-only picker (no MT5 fields, no link fee, no joining fee) */}
      <RenewPlanPicker
        accountType={type}
        acct={acct}
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        onSuccess={() => { setTopupOpen(false); onRefresh && onRefresh(); }}
        profile={profile}
      />

      {/* Connect / Update credentials modal */}
      <ConnectAccountDialog
        accountType={type}
        acct={acct}
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onSuccess={() => { setConnectOpen(false); onRefresh && onRefresh(); }}
      />

      {/* Delete confirmation */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid={`mgmt-delete-confirm-${type}`}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
        >
          <div className="w-full max-w-sm rounded-xl border border-rose-500/30 bg-zinc-950 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={18} weight="duotone" className="text-rose-400" />
              <h3 className="text-sm font-bold text-white">Delete {meta.label}?</h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
              This will release your MetaApi cloud container and unlink the MT5 account. Copy-trading will stop immediately.
              Re-linking later requires the one-time <span className="text-[#00E5FF] font-semibold">₹300 activation fee</span> and a fresh subscription.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="flex-1 py-2 rounded-md border border-white/10 text-[11px] text-zinc-300 hover:border-white/30"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid={`mgmt-delete-confirm-btn-${type}`}
                className="flex-1 py-2 rounded-md bg-rose-500 hover:bg-rose-400 text-white text-[11px] font-semibold disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Connect / Update MT5 credentials. Uses the existing ₹300 link-fee order
// when broker/login/server changes (i.e. different MT5 account), and is
// free when only the password is being rotated for the SAME account.
function ConnectAccountDialog({ accountType, acct, open, onClose, onSuccess }) {
  const [broker, setBroker] = useState(acct?.broker_name || "Exness");
  const [loginId, setLoginId] = useState(acct?.mt5_login_id || "");
  const [pwd, setPwd] = useState("");
  const [server, setServer] = useState(acct?.mt5_server || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setBroker(acct?.broker_name || "Exness");
    setLoginId(acct?.mt5_login_id || "");
    setServer(acct?.mt5_server || "");
    setPwd("");
    setError("");
  }, [open, acct]);

  if (!open) return null;

  const sameAccount =
    !!acct &&
    (acct.broker_name || "") === broker.trim() &&
    (acct.mt5_login_id || "") === loginId.trim() &&
    (acct.mt5_server || "") === server.trim();
  const needsLinkFee = !sameAccount; // different account → ₹300

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!broker.trim() || !loginId.trim() || !pwd.trim() || !server.trim()) {
      setError("Fill all MT5 broker fields");
      return;
    }
    setBusy(true);
    try {
      // Same account → just password rotation. Hit the free endpoint.
      if (!needsLinkFee) {
        await axios.post(
          `${AFF_API}/affiliate/mt5/${accountType}/update-password`,
          {
            broker_name: broker.trim(),
            mt5_login_id: loginId.trim(),
            mt5_password: pwd,
            mt5_server: server.trim(),
          },
          { headers: getAffHeaders() },
        );
        toast.success("Password updated. Trading continues uninterrupted.");
        onSuccess();
        return;
      }
      // Different account → ₹300 link-fee flow (re-uses resubmit pipeline).
      const orderRes = await axios.post(
        `${AFF_API}/affiliate/mt5-link-fee/create-order`,
        {
          action: `resubmit_${accountType}`,
          broker_name: broker.trim(),
          mt5_login_id: loginId.trim(),
          mt5_password: pwd,
          mt5_server: server.trim(),
        },
        { headers: getAffHeaders() },
      );
      const { order_id, amount_paise, key_id, stub } = orderRes.data;

      // 2a) Stub mode: verify directly
      if (stub) {
        await axios.post(
          `${AFF_API}/affiliate/mt5-link-fee/verify`,
          { razorpay_order_id: order_id, razorpay_payment_id: `stub_pay_${Date.now()}` },
          { headers: getAffHeaders() },
        );
        toast.success("Account credentials updated (₹300 link fee — test-stub). Reconnecting…");
        onSuccess();
        return;
      }

      // 2b) Live Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) { setError("Razorpay SDK failed to load"); return; }
      const rzp = new window.Razorpay({
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount_paise,
        currency: "INR",
        name: "VeloxAlgoTech LLP",
        description: `Connect MT5 · ${accountType.toUpperCase()} (${broker})`,
        order_id,
        theme: { color: "#00E5FF" },
        handler: async (response) => {
          try {
            await axios.post(
              `${AFF_API}/affiliate/mt5-link-fee/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAffHeaders() },
            );
            toast.success("Account credentials updated. Reconnecting…");
            onSuccess();
          } catch (err) {
            setError(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={`connect-dialog-${accountType}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl border border-sky-500/30 bg-zinc-950 p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-white">Connect / Update MT5 Account</h3>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              {needsLinkFee
                ? <>Linking a different MT5 account — a one-time <span className="text-[#00E5FF] font-semibold">₹300 link fee</span> applies.</>
                : "Rotating the password on your existing account — no fee."}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={16} /></button>
        </div>
        {error && <div className="mb-3 p-2 rounded bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300">{error}</div>}
        <form onSubmit={submit} className="space-y-3">
          <MT5CredentialsFields
            broker={broker} setBroker={setBroker}
            server={server} setServer={setServer}
            loginId={loginId} setLoginId={setLoginId}
            pwd={pwd} setPwd={setPwd}
            testidPrefix={`connect-${accountType}`}
          />
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-md border border-white/10 text-xs text-zinc-300 hover:border-white/30">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid={`connect-submit-${accountType}`}
              className="flex-1 py-2 rounded-md bg-sky-400 hover:bg-sky-300 text-black text-xs font-semibold disabled:opacity-50"
            >
              {busy ? "Processing…" : (needsLinkFee ? "Pay ₹300 & Connect" : "Update Password")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Renew-only plan picker — used by the Topup/Renew button on the Account
// Management box. Shows ONLY the 4 plan tiers (no MT5 broker form, no
// ₹300 link fee, no joining fee), since the user is renewing an existing
// linked + paid account. Backend automatically detects same-account
// renewal and waives both extra fees.
function RenewPlanPicker({ accountType, acct, open, onClose, onSuccess, profile }) {
  const [pricing, setPricing] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedPlan("starter");
    axios.get(`${AFF_API}/affiliate/mt5/pricing`)
      .then((r) => setPricing((r.data.plans || []).filter((p) => p.account_type === accountType)))
      .catch(() => setPricing([]));
  }, [open, accountType]);

  if (!open) return null;

  const plan = pricing.find((p) => p.plan === selectedPlan);
  const planFee = plan?.price_inr || 0;
  const Icon = ACCOUNT_META[accountType].icon;
  const accent = ACCOUNT_META[accountType].accent;

  const handleSubmit = async () => {
    if (!acct) {
      toast.error("No active account to renew");
      return;
    }
    setSubmitting(true);
    try {
      // Reuse the existing broker/login/server/password — backend treats this
      // as a same-account renewal and waives the ₹300 link fee + joining fee.
      const orderRes = await axios.post(
        `${AFF_API}/affiliate/mt5/${accountType}/create-order`,
        {
          plan: selectedPlan,
          broker_name: acct.broker_name,
          mt5_login_id: acct.mt5_login_id,
          mt5_password: acct.mt5_password,
          mt5_server: acct.mt5_server,
        },
        { headers: getAffHeaders() },
      );
      const { order_id, amount_paise, key_id, stub } = orderRes.data;

      // Stub mode: directly verify
      if (stub) {
        await axios.post(
          `${AFF_API}/affiliate/mt5/${accountType}/verify`,
          { razorpay_order_id: order_id, razorpay_payment_id: `stub_pay_${Date.now()}` },
          { headers: getAffHeaders() },
        );
        toast.success(`Subscription renewed · ${plan?.label || ""}`);
        onSuccess();
        return;
      }

      // Live Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Razorpay SDK failed to load"); return; }
      const rzp = new window.Razorpay({
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount_paise,
        currency: "INR",
        name: "VeloxAlgoTech LLP",
        description: `${ACCOUNT_META[accountType].label} · ${plan?.label || ""} renewal`,
        order_id,
        prefill: {
          name: profile?.name || "",
          email: profile?.email || "",
          contact: profile?.phone || "",
        },
        theme: { color: "#00E5FF" },
        handler: async (response) => {
          try {
            await axios.post(
              `${AFF_API}/affiliate/mt5/${accountType}/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAffHeaders() },
            );
            toast.success("Subscription renewed");
            onSuccess();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={`renew-picker-${accountType}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-white/10 bg-zinc-950 p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A` }}>
              <Icon size={22} weight="duotone" style={{ color: accent }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                Topup / Renew {ACCOUNT_META[accountType].label}
              </h2>
              <p className="text-[11px] text-zinc-500">
                MT5 Login <span className="text-zinc-300 font-mono">{acct?.mt5_login_id || "—"}</span>
                {" · "}{acct?.broker_name} · {acct?.mt5_server}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid={`renew-picker-close-${accountType}`} className="p-1 text-zinc-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Plan selection only */}
        <div className="mb-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">Choose a Plan</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {PLAN_META.map((p) => {
              const match = pricing.find((x) => x.plan === p.code);
              const isBest = p.code === "annual";
              const active = selectedPlan === p.code;
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => setSelectedPlan(p.code)}
                  data-testid={`renew-plan-${accountType}-${p.code}`}
                  className={`relative p-3 rounded-lg border text-left transition-all overflow-hidden ${
                    active
                      ? "border-[#00E5FF] bg-[#00E5FF]/8 shadow-[0_0_18px_rgba(0,229,255,0.15)]"
                      : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                  }`}
                >
                  {isBest && (
                    <span className="absolute -top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-[#F59E0B] text-black">
                      <Sparkle size={8} weight="fill" /> Best Value
                    </span>
                  )}
                  <p className="text-xs font-bold text-white">{p.label}</p>
                  <p className="text-[9px] text-zinc-500 mt-0.5 leading-snug">{p.tagline}</p>
                  <p className="text-lg font-black text-[#00E5FF] mt-2 font-mono">
                    ₹{match?.price_inr?.toLocaleString("en-IN") || "—"}
                  </p>
                  <p className="text-[9px] text-emerald-400/80 mt-0.5">{p.days} days · all-inclusive (no extra GST)</p>
                  {p.free_days > 0 && (
                    <span className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      + {p.free_days} free days
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Simple total — just the plan fee */}
        <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-1 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-400">{plan?.label || ""} subscription</span>
            <span className="text-white font-mono">₹{planFee.toLocaleString("en-IN")}</span>
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-emerald-300">✓ Link fee waived (existing account)</span>
            <span className="text-emerald-300 font-mono">₹0</span>
          </div>
          <div className="h-px bg-white/5 my-1.5"></div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-white">Total payable</span>
            <span className="text-lg font-black text-[#00E5FF] font-mono" data-testid={`renew-total-${accountType}`}>
              ₹{planFee.toLocaleString("en-IN")}
            </span>
          </div>
          <p className="text-[10px] text-emerald-400/80 mt-0.5 text-right">All-inclusive · No additional GST</p>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !planFee}
          data-testid={`renew-pay-${accountType}`}
          className="w-full py-3 rounded-md text-sm font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? "Processing..." : <>Pay ₹{planFee.toLocaleString("en-IN")} with Razorpay <ArrowRight size={14} weight="bold" /></>}
        </button>
        <p className="text-[10px] text-zinc-600 text-center mt-2">
          Secure checkout via Razorpay · UPI · Cards · Netbanking
        </p>
      </div>
    </div>
  );
}


function AddExtraDialog({ open, onClose, onAdded }) {
  const [accountType, setAccountType] = useState("cent");
  const [broker, setBroker] = useState("Exness");
  const [login, setLogin] = useState("");
  const [pwd, setPwd] = useState("");
  const [server, setServer] = useState("");
  const [nickname, setNickname] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [pricing, setPricing] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Fetch the 4-tier pricing once dialog opens
  useEffect(() => {
    if (!open) return;
    axios.get(`${AFF_API}/affiliate/mt5/pricing`)
      .then((r) => setPricing(r.data.plans || []))
      .catch(() => setPricing([]));
  }, [open]);

  if (!open) return null;

  const planRow = pricing.find((p) => p.account_type === accountType && p.plan === selectedPlan);
  const planFee = planRow?.price_inr ?? 0;
  const LINK_FEE = 300;
  const totalPayable = planFee + LINK_FEE;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      // Use the regular MT5 subscription create-order endpoint — it already bundles
      // the ₹300 link fee + plan fee + (joining fee if not yet paid).
      const orderRes = await axios.post(
        `${AFF_API}/affiliate/mt5/${accountType}/create-order`,
        {
          plan: selectedPlan,
          broker_name: broker,
          mt5_login_id: login,
          mt5_password: pwd,
          mt5_server: server,
          nickname: nickname || undefined,
        },
        { headers: getAffHeaders() },
      );
      const { order_id, amount_paise, amount_inr, key_id, stub } = orderRes.data;

      // 2a) Stub mode: directly verify
      if (stub) {
        await axios.post(
          `${AFF_API}/affiliate/mt5/${accountType}/verify`,
          { razorpay_order_id: order_id, razorpay_payment_id: `stub_pay_${Date.now()}` },
          { headers: getAffHeaders() },
        );
        toast.success(`Additional MT5 linked · ₹${amount_inr} paid (link fee + subscription)`);
        onAdded();
        return;
      }

      // 2b) Live Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) { setError("Razorpay SDK failed to load"); return; }
      const rzp = new window.Razorpay({
        key: key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount_paise,
        currency: "INR",
        name: "VeloxAlgoTech LLP Affiliate Platform",
        description: `Additional MT5 · ${accountType.toUpperCase()} · ${planRow?.label || ""} + ₹300 link fee`,
        order_id,
        theme: { color: "#00E5FF" },
        handler: async (response) => {
          try {
            await axios.post(
              `${AFF_API}/affiliate/mt5/${accountType}/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAffHeaders() },
            );
            toast.success("Additional MT5 linked successfully");
            onAdded();
          } catch (err) {
            setError(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to link account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="add-extra-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-xl border border-sky-500/30 bg-zinc-950 p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-white">Link Additional MT5 Account</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              One-time ₹{LINK_FEE} link fee + chosen subscription plan. All-inclusive · No additional GST.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={16} /></button>
        </div>
        {error && <div className="mb-3 p-2 rounded bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300">{error}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Type</label>
              <select value={accountType} onChange={(e) => setAccountType(e.target.value)} data-testid="add-extra-type"
                className="w-full px-2 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-xs text-white">
                <option value="cent">CENT</option>
                <option value="usd">USD</option>
              </select>
            </div>
            <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Nickname (optional)" data-testid="add-extra-nickname" className="bg-zinc-900 border-zinc-800 text-xs text-white" />
          </div>

          {/* Subscription plan selector */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-1.5 block">Subscription plan</label>
            <div className="grid grid-cols-2 gap-2" data-testid="add-extra-plan-grid">
              {PLAN_META.map((p) => {
                const row = pricing.find((x) => x.account_type === accountType && x.plan === p.code);
                const active = selectedPlan === p.code;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setSelectedPlan(p.code)}
                    data-testid={`add-extra-plan-${p.code}`}
                    className={`text-left p-2 rounded-md border transition ${
                      active ? "border-[#00E5FF] bg-[#00E5FF]/8" : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                    }`}
                  >
                    <p className="text-[11px] font-bold text-white">{p.label}</p>
                    <p className="text-[14px] font-black text-[#00E5FF] font-mono mt-0.5">₹{row?.price_inr?.toLocaleString("en-IN") || "—"}</p>
                    <p className="text-[9px] text-zinc-500">{p.days} days{p.free_days > 0 ? ` · +${p.free_days} free` : ""}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <MT5CredentialsFields
            broker={broker} setBroker={setBroker}
            server={server} setServer={setServer}
            loginId={login} setLoginId={setLogin}
            pwd={pwd} setPwd={setPwd}
            testidPrefix="add-extra"
          />

          {/* Price breakdown */}
          <div className="p-2.5 rounded-md bg-white/[0.02] border border-white/5 text-[11px] space-y-0.5" data-testid="add-extra-breakdown">
            <div className="flex justify-between"><span className="text-zinc-400">{planRow?.label || "—"} subscription</span><span className="text-white font-mono">₹{planFee.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">MT5 link fee (one-time)</span><span className="text-white font-mono">₹{LINK_FEE}</span></div>
            <div className="h-px bg-white/5 my-1"></div>
            <div className="flex justify-between"><span className="text-xs font-bold text-white">Total payable</span><span className="text-base font-black text-[#00E5FF] font-mono" data-testid="add-extra-total">₹{totalPayable.toLocaleString("en-IN")}</span></div>
            <p className="text-[10px] text-emerald-400/80 mt-0.5 text-right">All-inclusive · No additional GST</p>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-md border border-white/10 text-xs text-zinc-300 hover:border-white/30">Cancel</button>
            <button type="submit" disabled={busy || !planFee} data-testid="add-extra-submit"
              className="flex-1 py-2 rounded-md bg-sky-400 hover:bg-sky-300 text-black text-xs font-semibold disabled:opacity-50">
              {busy ? "Processing…" : `Pay ₹${totalPayable.toLocaleString("en-IN")} & Link`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
